// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React from "react"
import Header from "../header/HeaderComponent"
import Footer from "../footer/FooterComponent"
import Seo from "../Seo/SeoComponent"
import styles from "./releasesInfoPage.module.scss"

function ReleasesInfoPage() {
    return (
        <div className={styles.page}>
            <Seo
                title="Release Information"
                description="See how NihongoTech has grown and which features are included in each release."
                path="/releases"
            />
            <Header />

            <main className={styles.main}>
                <section className={styles.container}>
                    <h1 className={styles.title}>Release Information</h1>
                    <p className={styles.description}>
                        See how NihongoTech has grown and which features are included in each release.
                    </p>

                    <div className={`${styles.card} ${styles.currentCard}`}>
                        <div className={styles.cardHeader}>
                            <div>
                                <p className={styles.releaseLabel}>Current release · Updated September 20, 2026</p>
                                <h2 className={styles.cardTitle}>Version 2.2.1</h2>
                            </div>
                            <span className={styles.currentBadge}>Latest</span>
                        </div>
                        <p className={styles.cardText}>
                            Find official 2026 JLPT overseas test sites alongside NihongoTech's existing learning
                            tools, with clear session and host-institution details.
                        </p>
                        <h3 className={styles.sectionTitle}>What's new</h3>
                        <ul className={styles.featureList}>
                            <li>New: browse the official JLPT overseas directory by country and city, including
                                local host institutions, registration links, and available contact details</li>
                            <li>July 5 and December 6, 2026 test dates are shown alongside each location's
                                session availability, so cities that only test once are clear</li>
                            <li>When a city uses different organisers for July and December, each institution is
                                shown separately - including Madrid's Universidad Complutense and Universidad
                                Autónoma contacts</li>
                            <li>Directory entries retain official source and verification metadata, country notes,
                                and temporary suspension or pending-host status where published</li>
                            <li>New: achievements! Earn round turtle badges as you study - starting with your
                                first completed lesson, first kanji marked known, first vocabulary word, and
                                first grammar point learned, each with its own icon and color</li>
                            <li>An "Achievement unlocked!" notice appears the moment you earn one, and your
                                earned badges show on the homepage and on your profile, alongside the ones you
                                haven't unlocked yet</li>
                            <li>New: celebrate first-time lesson and grammar completions with XP, a quick way to
                                continue learning, and an optional Bluesky post</li>
                            <li>Level-ups now receive a friendly turtle celebration, without repeating after a
                                refresh or when progress updates more than once</li>
                            <li>The homepage's learning stats now show how many kanji, lessons, grammar points,
                                and vocabulary words each stat is counting, not just the XP</li>
                            <li>WaniKani now has its own "Integrations" section on the homepage, separate from
                                "Start learning"</li>
                        </ul>
                    </div>

                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div>
                                <p className={styles.releaseLabel}>Updated September 16, 2026</p>
                                <h2 className={styles.cardTitle}>Version 2.1.8</h2>
                            </div>
                        </div>
                        <p className={styles.cardText}>
                            Introducing learning levels and XP, a homepage that shows how far you've come, richer
                            writing practice, and translation in both directions between Japanese and English.
                        </p>
                        <h3 className={styles.sectionTitle}>What's new</h3>
                        <ul className={styles.featureList}>
                            <li>Earn XP and level up as you study - your level now appears in the header, and
                                flawless writing practice earns bonus XP</li>
                            <li>Returning learners see their level, XP, and top study areas right on the
                                homepage</li>
                            <li>New progress bars for Hiragana, Katakana, Kanji, Grammar, and Vocabulary under
                                "Start learning"</li>
                            <li>All learning paths are now together in one "Start learning" section, with guided
                                lessons marked as the place to start</li>
                            <li>Kana writing practice now opens with an animated, looping stroke order preview and
                                shows your practice progress</li>
                            <li>Each kana now shows your practice level: Beginner, Intermediate, Advanced, or
                                Epic!</li>
                            <li>Thicker drawn strokes and a brush-dot cursor in the writing quiz</li>
                            <li>Paste an image from your clipboard into the translator with Ctrl+V or the new
                                "Paste image" button</li>
                            <li>Switch the translator to translate typed English text into Japanese</li>
                            <li>Fixed uploaded and pasted images appearing stretched in the translator</li>
                            <li>Larger, easier-to-read text on the verb conjugation page</li>
                            <li>Renamed the kanji writing practice card to "Draw kanji"</li>
                            <li>Improved level badge contrast and kept the admin stats dashboard reachable on
                                mobile</li>
                            <li>The learning progress tiles now fill the width of the "Start learning" section</li>
                            <li>Check your answers in the Kanji Memory Quiz: each reading and meaning is marked
                                right, wrong, or left blank, and the ones you missed show the correct answer</li>
                            <li>Mark a verb's conjugations as learned, and hear any conjugated form read aloud</li>
                            <li>The conjugation table is now a proper table, with a hidden romaji column you can
                                reveal row by row and a count of how often you've listened to each form</li>
                            <li>Filter and search kanji writing practice by category, with bigger kanji in the
                                grid</li>
                            <li>A "new version available" banner now replaces the silent page reload</li>
                            <li>Listen at a slower speed: a new turtle button plays pronunciation at 0.6x
                                speed anywhere audio already plays - vocabulary, kanji, conjugation, grammar,
                                lessons, and writing/speaking practice</li>
                        </ul>
                    </div>

                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div>
                                <p className={styles.releaseLabel}>Updated September 13, 2026</p>
                                <h2 className={styles.cardTitle}>Version 2.1.7</h2>
                            </div>
                        </div>
                        <p className={styles.cardText}>
                            A big pass of homepage and navigation polish, plus a new 5-minute study session timer
                            from かめ.
                        </p>
                        <h3 className={styles.sectionTitle}>What's new</h3>
                        <ul className={styles.featureList}>
                            <li>かめ can now start a 5-minute study session with a live countdown clock, and
                                congratulates you when it's done</li>
                            <li>The footer collapses behind a "Show more" toggle on mobile instead of taking up
                                the whole screen</li>
                            <li>Shortened and simplified homepage descriptions across the featured tools,
                                Japanese tools, and reference sections</li>
                            <li>The translate spotlight card is now a simpler two-option layout: translate from
                                text or from an image</li>
                            <li>The Japanese tools grid now shows 2 cards per row on mobile, matching the other
                                homepage grids</li>
                            <li>Fixed the "Continue" button on in-progress lessons being too small on mobile and
                                too wide on desktop</li>
                            <li>Swapped the kanji writing practice icon for a brush, and cleaned up the mobile
                                header's "Show more" button</li>
                        </ul>
                    </div>

                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div>
                                <p className={styles.releaseLabel}>Updated September 9, 2026</p>
                                <h2 className={styles.cardTitle}>Version 2.1.6</h2>
                            </div>
                        </div>
                        <p className={styles.cardText}>
                            Added personal lesson notes saved to your account, improved page scrolling and account
                            feedback, and made cookie preferences easier to access.
                        </p>
                        <h3 className={styles.sectionTitle}>What's new</h3>
                        <ul className={styles.featureList}>
                            <li>Add, edit, and delete personal lesson notes, with cloud sync through your account</li>
                            <li>Fixed the page background while scrolling through long lessons</li>
                            <li>Fixed navigation to the admin stats dashboard</li>
                            <li>Open cookie preferences directly from the footer</li>
                            <li>Updated privacy disclosures and measurement consent handling</li>
                            <li>Added localized loading feedback while signing out</li>
                        </ul>
                    </div>

                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div>
                                <p className={styles.releaseLabel}>Updated September 6, 2026</p>
                                <h2 className={styles.cardTitle}>Version 2.1.5</h2>
                            </div>
                        </div>
                        <p className={styles.cardText}>
                            Fixed a jarring double page reload right after a deploy, and smoothed out how the header
                            shows sign-in status while it loads.
                        </p>
                        <h3 className={styles.sectionTitle}>What's new</h3>
                        <ul className={styles.featureList}>
                            <li>Removed a visible double reload that could occur when a browser was still running
                                an older version of the site</li>
                            <li>Added a loading spinner to the header's account area while sign-in status is still
                                being checked, so it no longer pops in without warning</li>
                        </ul>
                    </div>

                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div>
                                <p className={styles.releaseLabel}>Updated September 4, 2026</p>
                                <h2 className={styles.cardTitle}>Version 2.1.4</h2>
                            </div>
                        </div>
                        <p className={styles.cardText}>
                            Improved lesson progress feedback and strengthened the public site, API, and account
                            security controls.
                        </p>
                        <h3 className={styles.sectionTitle}>What's new</h3>
                        <ul className={styles.featureList}>
                            <li>Search grammar rules by pattern, meaning, explanation, formation, or example</li>
                            <li>Find kana grammar patterns using romaji, including searches such as “tekudasai”</li>
                            <li>Spacing- and punctuation-insensitive grammar search</li>
                            <li>Persistent lesson completion tracking with a learned-progress summary</li>
                            <li>Topic search across lessons, including common aliases and related terms</li>
                            <li>Corrected punctuation-key overflow on laptop-sized keyboard layouts</li>
                            <li>Persistent learned badges and completed styling on collapsed lesson cards</li>
                            <li>Browser security headers with Content Security Policy monitoring</li>
                            <li>Rate-limited, WAF-protected public APIs with strict request validation</li>
                            <li>Signed newsletter unsubscribe links and Lambda concurrency cost controls</li>
                            <li>Optional authenticator-app MFA, Cognito threat auditing, and stronger passwords</li>
                            <li>Patched React Router security vulnerabilities</li>
                        </ul>
                    </div>

                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div>
                                <p className={styles.releaseLabel}>Updated September 2, 2026</p>
                                <h2 className={styles.cardTitle}>Version 2.1.2</h2>
                            </div>
                        </div>
                        <p className={styles.cardText}>
                            Added an extensive lesson on counting in Japanese, including the different counters
                            used for people, animals, objects, dates, time, durations, and age.
                        </p>
                        <h3 className={styles.sectionTitle}>What's new</h3>
                        <ul className={styles.featureList}>
                            <li>Core number readings and the general ～つ counting system</li>
                            <li>Fifteen everyday counters for people, animals, long and flat objects, books,
                                vehicles, buildings, and more</li>
                            <li>A dedicated 人 table for counting one to ten people, asking 何人, and using the
                                formal 名 counter</li>
                            <li>Pronunciation-change tables for 本, 匹, 杯, 冊, and 回</li>
                            <li>Guidance for counting dates, time, durations, calendar months, years, and age</li>
                            <li>Natural example sentences and a practical learning strategy</li>
                        </ul>
                    </div>

                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div>
                                <p className={styles.releaseLabel}>Updated August 30, 2026</p>
                                <h2 className={styles.cardTitle}>Version 2.0.0</h2>
                            </div>
                        </div>
                        <p className={styles.cardText}>
                            Added user accounts with cloud progress sync, and a Japanese grammar section. This
                            release includes every feature that was available in NihongoTech at launch.
                        </p>
                        <h3 className={styles.sectionTitle}>Features introduced</h3>
                        <ul className={styles.featureList}>
                            <li>On-screen hiragana and katakana keyboards</li>
                            <li>Basic, voiced, combination, and punctuation character groups</li>
                            <li>Romaji input with automatic hiragana or katakana conversion</li>
                            <li>Common kanji suggestions for typed kana</li>
                            <li>A dedicated hiragana-to-kanji converter for beginner vocabulary</li>
                            <li>A romaji-to-kana converter with a hiragana/katakana mode toggle</li>
                            <li>Direct hiragana, romaji, and on-screen keyboard input in the converter</li>
                            <li>Selectable conversion results and output preview</li>
                            <li>Example Japanese phrases and converter words for quick input</li>
                            <li>Delete, clear, and copy-to-clipboard controls</li>
                            <li>Keyboard visibility controls on smaller screens</li>
                            <li>Responsive layouts for desktop and mobile screens</li>
                            <li>Lessons covering kana and kanji basics</li>
                            <li>A Japanese grammar section with N5-N4 grammar points</li>
                            <li>A full jōyō and jinmeiyō kanji reference with readings, stroke order, and
                                pronunciation audio</li>
                            <li>JLPT N5-N1 filtering for the kanji list and the Kanji Memory Quiz</li>
                            <li>A Kanji Memory Quiz with meaning, reading, and stroke-order exercises, and
                                onyomi/kunyomi reading feedback</li>
                            <li>Hiragana and katakana writing practice, with a per-kanji writing counter</li>
                            <li>A Japanese to English translator, from typed text or a photo</li>
                            <li>An optional WaniKani integration for a kanji study progress dashboard</li>
                            <li>User accounts with email/password or Google sign-in</li>
                            <li>Cloud sync of learning progress across devices</li>
                            <li>A profile page and a site stats page</li>
                            <li>A blog</li>
                            <li>About, release notes, and privacy policy pages</li>
                        </ul>
                    </div>

                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div>
                                <p className={styles.releaseLabel}>Updated August 28, 2026</p>
                                <h2 className={styles.cardTitle}>Version 1.4.0</h2>
                            </div>
                        </div>
                        <p className={styles.cardText}>
                            Added lessons, a jōyō and jinmeiyō kanji reference with stroke order and audio, and an
                            optional WaniKani integration for tracking kanji study progress.
                        </p>
                        <h3 className={styles.sectionTitle}>Features introduced</h3>
                        <ul className={styles.featureList}>
                            <li>Lessons covering kana and kanji basics</li>
                            <li>A full jōyō and jinmeiyō kanji reference with readings, stroke order, and
                                pronunciation audio</li>
                            <li>JLPT N5-N1 filtering for the kanji list and the Kanji Memory Quiz</li>
                            <li>A Kanji Memory Quiz to self-assess kanji you already know, with meaning, reading,
                                and stroke-order exercises</li>
                            <li>Hiragana and katakana writing practice, with a per-kanji writing counter</li>
                            <li>A Japanese to English translator, from typed text or a photo</li>
                            <li>An optional WaniKani integration for a kanji study progress dashboard</li>
                            <li>A blog and a site stats page</li>
                            <li>About, release notes, and privacy policy pages</li>
                        </ul>
                    </div>

                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div>
                                <p className={styles.releaseLabel}>Updated August 12, 2026</p>
                                <h2 className={styles.cardTitle}>Version 1.3.0</h2>
                            </div>
                        </div>
                        <p className={styles.cardText}>
                            Added a dedicated romaji-to-kana converter.
                        </p>
                        <h3 className={styles.sectionTitle}>Features introduced</h3>
                        <ul className={styles.featureList}>
                            <li>A romaji-to-kana converter with a hiragana/katakana mode toggle</li>
                        </ul>
                    </div>

                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div>
                                <p className={styles.releaseLabel}>Updated August 6, 2026</p>
                                <h2 className={styles.cardTitle}>Version 1.2.0</h2>
                            </div>
                        </div>
                        <p className={styles.cardText}>
                            Expanded the app into a fuller learning and conversion tool.
                        </p>
                        <h3 className={styles.sectionTitle}>Features introduced</h3>
                        <ul className={styles.featureList}>
                            <li>On-screen hiragana and katakana keyboards</li>
                            <li>Basic, voiced, combination, and punctuation character groups</li>
                            <li>Romaji input with automatic hiragana or katakana conversion</li>
                            <li>Common kanji suggestions for typed kana</li>
                            <li>A dedicated hiragana-to-kanji converter for beginner vocabulary</li>
                            <li>Direct hiragana, romaji, and on-screen keyboard input in the converter</li>
                            <li>Selectable conversion results and output preview</li>
                            <li>Example Japanese phrases and converter words for quick input</li>
                            <li>Delete, clear, and copy-to-clipboard controls</li>
                            <li>Keyboard visibility controls on smaller screens</li>
                            <li>Responsive layouts for desktop and mobile screens</li>
                            <li>About, release notes, and privacy policy pages</li>
                        </ul>
                    </div>

                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div>
                                <p className={styles.releaseLabel}>Initial release</p>
                                <h2 className={styles.cardTitle}>Version 1.0.0</h2>
                            </div>
                        </div>
                        <p className={styles.cardText}>
                            Introduced the core Japanese typing experience with on-screen character input and
                            essential text controls.
                        </p>
                        <h3 className={styles.sectionTitle}>Features introduced</h3>
                        <ul className={styles.featureList}>
                            <li>On-screen Japanese character keyboards</li>
                            <li>Text composition area</li>
                            <li>Delete and clear controls</li>
                            <li>Copy-to-clipboard support</li>
                        </ul>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    )
}

export default ReleasesInfoPage
